import React, {useEffect, useState, useCallback, useRef} from "react";
import {useSelector, useDispatch} from "react-redux";
import {Progress, Button} from "antd";

import {i18, trb, Logout} from "@utilities";
import {selectLoginRequestInfo, AuthActions} from "@redux";
import useInterval from "@hooks/useInterval";
import {allLoginStatus, history, routerPath, STORAGE_ITEMS} from "@config";
import {Notification, NOTIFICATION_TYPES} from "..";
import {SigningModal} from "./SigningModal";

const INITIA_MODAL_STATE = {
    isOpen: false,
    modalType: null
};

const clearLoginStorage = () => {
    localStorage.removeItem(STORAGE_ITEMS.LOGIN_TOKEN);
    localStorage.removeItem(STORAGE_ITEMS.SCA_TIMEOUT);
};

export const Signing = React.memo(({applicationSign, submitReset, isWaitingRoom}) => {
    const dispatch = useDispatch();
    const {scaTimeout, controlCode} = useSelector(selectLoginRequestInfo);
    
    const [timer, setTimer] = useState();
    const [isScaIntervalPlaying, setIsScaIntervalPlaying] = useState(false);
    const [modal, setModal] = useState(INITIA_MODAL_STATE);
    const mountedOnce = useRef(false);
    
    useInterval(() => setTimer(prev => prev - 1), timer ? 1000 : null);
    useInterval(async () => await handleCheckSca(), isScaIntervalPlaying ? 5000 : null);
    
    const resetTimers = () => {
        setTimer(undefined);
        setIsScaIntervalPlaying(false);
    };
    
    const handleLoginFlowCancel = () => {
        sessionStorage.clear();
        clearLoginStorage();
        resetTimers();
        setModal(INITIA_MODAL_STATE);
        Logout();
        if (!isWaitingRoom) submitReset();
    };
    
    useEffect(() => {
        if (timer === 0) {
            handleLoginFlowCancel();
        }
    }, [timer]);
    
    useEffect(() => {
        const scaTimeoutStorage = localStorage.getItem(STORAGE_ITEMS.SCA_TIMEOUT);
        if (scaTimeout || scaTimeoutStorage) {
            setTimer(scaTimeout || scaTimeoutStorage);
        }
    }, [scaTimeout]);
    
    useEffect(() => {
        if (mountedOnce.current) return;
        const loginToken = localStorage.getItem(STORAGE_ITEMS.LOGIN_TOKEN);
        // login token check is for waiting room
        if (!loginToken && !controlCode) {
            Notification({
                type: NOTIFICATION_TYPES.ERROR,
                message: trb(i18.NotificationTitles.TokenNotFound)
            });
            submitReset();
        }
        
        (async () => {
            // first request here in order
            // not to wait for interval tick
            try {
                await handleCheckSca();
                mountedOnce.current = true;
            } catch (e) {
                resetTimers();
                clearLoginStorage();
                submitReset();
            }
        })();
    }, [controlCode]);
    
    const handleCheckSca = useCallback(async () => {
        setIsScaIntervalPlaying(true);
        const response = await dispatch(AuthActions.checkSca(isWaitingRoom));
        const status = response?.payload?.status;
        switch (status) {
            case allLoginStatus.pending:
                break;
            case allLoginStatus.success:
                clearLoginStorage();
                resetTimers();
                history.push(routerPath.kyc);
                break;
            case allLoginStatus.selectCompany:
                resetTimers();
                clearLoginStorage();
                setModal({
                    isOpen: true,
                    modalType: allLoginStatus.selectCompany
                });
                break;
            case allLoginStatus.failed:
                resetTimers();
                submitReset();
                break;
            case allLoginStatus.noAuthorizeCompany:
                clearLoginStorage();
                resetTimers();
                setModal({
                    isOpen: true,
                    modalType: allLoginStatus.noAuthorizeCompany
                });
                break;
            // unrecognized status we interupt the timer
            default:
                resetTimers();
                clearLoginStorage();
                submitReset();
                break;
        }
        return response;
    }, []);
    
    if (applicationSign) {
        return (
            <div className="text-center w-100">
                <div className="mb-3">
                    {controlCode && (
                        <div className="eb_fs_16 eb_fw_500 mt-3">
                            {trb(i18.Labels.ControlCode)}&nbsp;
                            <strong>{controlCode}</strong>&nbsp;
                            {trb(i18.Labels.ControlCodeTimeLeft)}&nbsp;
                            <strong>{timer}</strong>s
                        </div>
                    )}
                </div>
            </div>
        );
    }
    
    return (
        <>
            <div className="text-center w-100">
                <div className="mb-3">
                    {controlCode && (
                        <div className="eb_fs_16 eb_fw_500 mt-3">
                            {trb(i18.Labels.ControlCode)}&nbsp;
                            <strong>{controlCode}</strong>&nbsp;
                            {trb(i18.Labels.ControlCodeTimeLeft)}&nbsp;
                            <strong>{timer}</strong>s
                        </div>
                    )}
                </div>
            </div>
            {modal.isOpen && <SigningModal type={modal.modalType} onCancel={handleLoginFlowCancel}/>}
        </>
    );
});
